import React from "react";
import {
  Delete,
  Edit,
  Group,
  Visibility,
  VisibilityOff,
  Star,
} from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import GroupsIcon from "@mui/icons-material/Groups";
import MarkdownPreview from "@uiw/react-markdown-preview";
import rehypeSanitize from "rehype-sanitize";
import {
  FormItem,
  ItemType as QuestionTypeEnum,
  ItemRecipientType,
  ItemSubjectType,
  ItemVisibility,
} from "../../types";
import { getLabelWithCharCount } from "../../Helpers";
import { getOptionIssues, optionProblemSummaries } from "./validation";

// FL-158: the template editor auto-saves, so a question can be collapsed (or
// the page reloaded) while its options are non-compliant — this flag keeps
// the problem visible outside the edit card. Editor-only: respondents see
// the live select, never editing affordances.
const OptionIssuesFlag = ({ formItem }: { formItem: FormItem }) => {
  const issues = getOptionIssues(formItem);
  if (issues.length === 0) {
    return null;
  }
  return (
    <Box
      role="alert"
      sx={{
        mt: 1,
        mb: 1,
        p: 1.5,
        display: "flex",
        gap: 1.5,
        alignItems: "center",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "warning.main",
      }}
    >
      <WarningAmberIcon fontSize="small" sx={{ color: "warning.main" }} />
      <Typography variant="body2">
        {`Fix this question's options before sending — ${optionProblemSummaries(
          issues,
        ).join(", and ")}.`}
      </Typography>
    </Box>
  );
};

export interface Props {
  editable?: boolean;
  formItem: FormItem;
  value?: number | string | null;
  onValueChange?: (answer: number | string) => void;
  onEditClick?: () => void;
  onDeleteClick?: () => void;
  sx?: SxProps;
  hideRecipientIndicators?: boolean;
}

// Shared by survey responses and Work Review reviews (FL-299) so optional
// questions carry one consistent badge everywhere.
export const OptionalLabel = ({ optional }: { optional?: boolean }) => {
  if (!optional) return null;
  return (
    <Typography
      sx={{
        display: "inline",
        color: "text.secondary",
        ml: 1,
        fontSize: "12px",
        border: "1px solid #666",
        borderRadius: "4px",
        padding: "1px 4px",
        backgroundColor: "#333",
      }}
    >
      Optional
    </Typography>
  );
};

// Shared visibility indicator (FL-299): the releasable eye / private
// crossed-eye shown next to a question label.
export const VisibilityIndicator = ({
  visibility,
}: {
  visibility?: FormItem["visibility"];
}) => {
  if (visibility == ItemVisibility.Private) {
    return (
      <Tooltip title="This question's results will only be visible to instructors">
        <VisibilityOff sx={{ ml: 1 }} />
      </Tooltip>
    );
  }
  if (visibility == ItemVisibility.Releasable) {
    return (
      <Tooltip title="This question's anonymized results are releasable to students">
        <Visibility sx={{ ml: 1 }} />
      </Tooltip>
    );
  }
  return null;
};

export const QuestionView = ({
  editable = true,
  formItem: question,
  value,
  onValueChange,
  onEditClick,
  onDeleteClick,
  sx,
  hideRecipientIndicators,
}: Props) => {
  const Interactions = () => (
    <Box
      sx={{
        display: "flex",
        mt: -1,
        mb: 0.5,
      }}
    >
      {onEditClick && (
        <Button
          disabled={!editable}
          variant="contained"
          sx={{ m: 2 }}
          onClick={() => onEditClick()}
          size="small"
        >
          Edit <Edit />
        </Button>
      )}
      {onDeleteClick && (
        <Button
          disabled={!editable}
          variant="contained"
          sx={{ m: 2, ml: 0 }}
          onClick={() => onDeleteClick()}
          size="small"
        >
          Delete <Delete />
        </Button>
      )}
    </Box>
  );

  if (question.itemType == QuestionTypeEnum.Instructions) {
    // TODO: Remove MarkdownPreview color overrides when auto-dark-mode is implemented

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 3,
          ...sx,
        }}
      >
        <MarkdownPreview
          source={question.label}
          style={{ backgroundColor: "inherit", color: "white" }}
          rehypePlugins={[rehypeSanitize]}
        />
        <Box sx={{ mt: -1.5 }}>
          <Interactions />
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <Box sx={{ display: "flex", flex: 1 }}>
          <Typography>
            {question.label.split("\n").map((x, index, arr) => (
              <span key={index}>
                {x}
                {index !== arr.length - 1 && <br />}
              </span>
            ))}
            <OptionalLabel optional={question.optional} />
          </Typography>
        </Box>

        {!hideRecipientIndicators &&
          question.recipientType == ItemRecipientType.TeamMembers && (
            <Tooltip title="This question will be received by all team members">
              <GroupsIcon sx={{ ml: 1 }} />
            </Tooltip>
          )}
        {!hideRecipientIndicators &&
          question.recipientType == ItemRecipientType.TeamLeaders && (
            <Tooltip title="This question will only be received by team leads">
              <Star sx={{ ml: 1 }} />
            </Tooltip>
          )}
        {!hideRecipientIndicators &&
          question.recipientType == ItemRecipientType.NonLeaders && (
            <Tooltip title="This question will only be received by non-leaders">
              <Group sx={{ ml: 1 }} />
            </Tooltip>
          )}
        <VisibilityIndicator visibility={question.visibility} />
        {question.subjectType == ItemSubjectType.PeerSelf && (
          <Tooltip title="This question is regarding peer (including self) performance during this course">
            <PeopleIcon sx={{ ml: 1 }} />
          </Tooltip>
        )}
        {question.subjectType == ItemSubjectType.LeadOnly && (
          <Tooltip title="This question is regarding team lead performance during this course">
            <Star sx={{ ml: 1 }} />
          </Tooltip>
        )}
        {question.subjectType == ItemSubjectType.LeadSelf && (
          <Tooltip title="This question is regarding team lead (including self) performance during this course">
            <PeopleIcon sx={{ ml: 1 }} />
          </Tooltip>
        )}
        {question.subjectType == ItemSubjectType.NonLeadSelf && (
          <Tooltip title="This question is regarding non-lead (including self) performance during this course">
            <PeopleIcon sx={{ ml: 1 }} />
          </Tooltip>
        )}
        {question.subjectType == ItemSubjectType.NonLeadOnly && (
          <Tooltip title="This question is regarding non-lead performance during this course">
            <PeopleIcon sx={{ ml: 1 }} />
          </Tooltip>
        )}
        {question.subjectType == ItemSubjectType.PeerOnly && (
          <Tooltip title="This question is regarding peer performance during this course">
            <PeopleIcon sx={{ ml: 1 }} />
          </Tooltip>
        )}
        {question.subjectType == ItemSubjectType.General && (
          <Tooltip title="This question is reflecting on the course in general and results only visible to the instructor">
            <ContactSupportIcon sx={{ ml: 1 }} />
          </Tooltip>
        )}
      </Box>
      {onEditClick && <OptionIssuesFlag formItem={question} />}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        {question.itemType == QuestionTypeEnum.Choice && (
          <>
            <FormControl
              fullWidth
              margin="normal"
              size="small"
              sx={{ mt: 1, mb: 2 }}
            >
              <InputLabel id={question.id}>Select an option</InputLabel>

              <Select
                labelId={question.id}
                id={`${question.id}-select`}
                value={value === undefined ? "" : value}
                label={"Select an option"}
                size="small"
                onChange={(e) =>
                  onValueChange && onValueChange(Number(e.target.value))
                }
              >
                {[...question.options]
                  .sort((a, b) => a.order - b.order)
                  .map((option, index) => (
                    <MenuItem key={index} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </>
        )}

        {question.itemType == QuestionTypeEnum.Text && (
          <TextField
            label={getLabelWithCharCount(
              "Enter a response",
              String(value ?? "").length,
            )}
            value={value === undefined || value === null ? "" : value}
            multiline
            onChange={(e) => onValueChange && onValueChange(e.target.value)}
            fullWidth={true}
            size="small"
            sx={{ mt: 1, mb: 2 }}
          />
        )}

        {question.itemType == QuestionTypeEnum.Points && (
          <FormControl
            fullWidth
            margin="normal"
            size="small"
            sx={{ mt: 1, mb: 2 }}
          >
            Points per student (baseline): {question.scale}
          </FormControl>
        )}

        <Interactions />
      </Box>
    </>
  );
};
